"use client";

import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
const languages = [{
  code: "EN",
  label: "English",
  flag: "EN"
}, {
  code: "ES",
  label: "Español",
  flag: "ES"
}, {
  code: "FR",
  label: "Français",
  flag: "FR"
}, {
  code: "IT",
  label: "Italiano",
  flag: "IT"
}];
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-sm font-medium tracking-wide text-foreground font-default transition-colors hover:text-primary hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-[1.5px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8 px-6">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 mt-10 px-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="block py-3 text-2xl font-default font-medium text-foreground border-b border-border/60 transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [activeLang, setActiveLang] = useState("EN");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="654" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out ${scrolled ? "bg-background/75 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ease-out ${scrolled ? "h-[72px]" : "h-[88px]"}`}>
        <div className="flex items-center flex-shrink-0">
          <Link to="/" aria-label="ITLS home" className="flex items-center">
            <WvcLogo className="h-10 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="78">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 gap-1.5 px-2 text-xs font-default font-medium tracking-wide text-foreground hover:bg-transparent hover:text-primary">
                <span className="inline-flex items-center justify-center h-5 px-1.5 rounded-lg bg-secondary text-secondary-foreground text-[10px] font-semibold tracking-wider">
                  {activeLang}
                </span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[10rem]">
              {languages.map((lang, i) => <DropdownMenuItem key={lang.code} data-index={i} onClick={() => setActiveLang(lang.code)} className="font-default text-sm cursor-pointer">
                  <span className="inline-flex items-center justify-center h-5 w-7 rounded-lg bg-secondary text-secondary-foreground text-[10px] font-semibold tracking-wider mr-2">
                    {lang.flag}
                  </span>
                  {lang.label}
                </DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/ConnectPage" className="text-sm font-default font-medium tracking-wide text-foreground transition-colors hover:text-primary">
            Client Portal
          </Link>

          <Button asChild size="sm" className="group h-10 px-5 rounded-lg font-default font-medium tracking-wide shadow-md bg-primary text-primary-foreground">
            <Link to="/ConnectPage">
              Inquire Now
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-foreground hover:bg-secondary" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background p-0 flex flex-col">
              <div className="flex items-center justify-between px-6 pt-6 pb-2">
                <Link to="/" aria-label="ITLS home" onClick={() => setMobileOpen(false)} className="flex items-center">
                  <WvcLogo className="h-10 w-auto" />
                </Link>
              </div>

              <div className="flex-1 overflow-y-auto">
                <MenuProvider menu_id="78">
                  <MobileMenuConsumer />
                </MenuProvider>

                <div className="mt-8 px-6">
                  <p className="text-xs font-default font-medium tracking-widest text-muted-foreground uppercase mb-3">
                    Language
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {languages.map((lang, i) => <Button key={lang.code} data-index={i} onClick={() => setActiveLang(lang.code)} variant={activeLang === lang.code ? "default" : "secondary"} size="sm" className="h-9 px-3 text-xs font-default font-semibold tracking-wider rounded-lg">
                        {lang.flag}
                      </Button>)}
                  </div>
                </div>
              </div>

              <div className="border-t border-border p-6 flex flex-col gap-3">
                <Button asChild variant="outline" className="w-full h-11 font-default font-medium tracking-wide rounded-lg">
                  <Link to="/ConnectPage" onClick={() => setMobileOpen(false)}>
                    Client Portal
                  </Link>
                </Button>
                <Button asChild className="group w-full h-11 font-default font-medium tracking-wide rounded-lg bg-primary text-primary-foreground">
                  <Link to="/ConnectPage" onClick={() => setMobileOpen(false)}>
                    Inquire Now
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}